/**
 * Dataset usage registry.
 *
 * Provides helpers to discover where a given dataset is used across the app:
 *  - Predefined category dashboards (Forwardmarkten/Flexmarkten) — derived by
 *    walking the panel registry.
 *  - Tools (Alerts, Forecasts, Benchmarks, Simulators) — hand-curated below.
 *
 * To add a tool that uses a specific dataset, append an entry to `toolsRegistry`
 * with either an explicit list of dataset IDs or the `ANY_DATASET` sentinel for
 * tools that can target any dataset (like Alerts).
 */

import { flexDashboards, forwardDashboards } from '@/features/visualizations/domain/metadata';
import { panelRegistry } from '@/features/visualizations/domain/panels';

export const ANY_DATASET = Symbol('any-dataset');

const dashboardMetadataIndex = {
  forward: Object.fromEntries(forwardDashboards.map((d) => [d.id, d])),
  flex: Object.fromEntries(flexDashboards.map((d) => [d.id, d])),
};

const toolsRegistry = [];

function resolvePanels(panelsOrFn) {
  if (typeof panelsOrFn === 'function') {
    try {
      return panelsOrFn({}) || [];
    } catch {
      return [];
    }
  }
  return Array.isArray(panelsOrFn) ? panelsOrFn : [];
}

/**
 * Find all predefined dashboards that include a panel referencing `datasetId`.
 *
 * Returns: Array<{ category, segmentKey, titleKey, descriptionKey, route }>
 */
export function getDashboardsUsingDataset(datasetId) {
  if (!datasetId) return [];

  const matches = [];

  for (const [category, segments] of Object.entries(panelRegistry)) {
    for (const [segmentKey, panelsOrFn] of Object.entries(segments)) {
      const panels = resolvePanels(panelsOrFn);
      const matched = panels.some((panel) => panel?.datasetId === datasetId);
      if (!matched) continue;

      const meta = dashboardMetadataIndex[category]?.[segmentKey];
      matches.push({
        category,
        segmentKey,
        titleKey: meta?.title ?? null,
        descriptionKey: meta?.description ?? null,
        route: meta?.route ?? `/reports/${category}/${segmentKey}`,
      });
    }
  }

  return matches;
}

/**
 * Find every dashboard that embeds a specific (datasetId, visualizationId)
 * panel. The match is narrower than `getDashboardsUsingDataset` — only
 * dashboards that include this exact visualization (not just any chart from
 * the dataset) are returned.
 *
 * Each match carries `panelId` so callers can deep-link into the panel via
 * query params (`?highlightPanel=…`), which the CategoryDashboard page
 * watches for and uses to scroll + flash the panel into view.
 *
 * Returns: Array<{ category, segmentKey, route, panelId }>
 */
export function getDashboardsUsingVisualization(datasetId, visualizationId) {
  if (!datasetId || !visualizationId) return [];
  const matches = [];
  for (const [category, segments] of Object.entries(panelRegistry)) {
    for (const [segmentKey, panelsOrFn] of Object.entries(segments)) {
      const panels = resolvePanels(panelsOrFn);
      const panel = panels.find(
        (p) => p?.datasetId === datasetId && p?.visualizationId === visualizationId,
      );
      if (!panel) continue;
      const meta = dashboardMetadataIndex[category]?.[segmentKey];
      matches.push({
        category,
        segmentKey,
        route: meta?.route ?? `/reports/${category}/${segmentKey}`,
        panelId: panel.id || null,
      });
    }
  }
  return matches;
}

/**
 * Resolve the best click-through target for a visualization preview embed
 * (e.g. `MiniGraphVisualization`). Priority:
 *   1. The first dashboard that already shows this exact `(datasetId,
 *      visualizationId)` panel — appended with `?highlightPanel={panelId}`
 *      so the consuming page can scroll/flash the panel into focus.
 *   2. The fullscreen single-viz view (`/dataset/.../fullscreen`) as a
 *      universal fallback so the user always lands on the chart in
 *      context — never on a generic dataset landing page.
 *   3. The dataset landing page when nothing else is available.
 *
 * Returns a route string suitable for `<router-link :to>`.
 */
export function resolveVisualizationDeepLink(datasetId, visualizationId) {
  if (!datasetId) return null;
  if (visualizationId) {
    const [dashboardMatch] = getDashboardsUsingVisualization(datasetId, visualizationId);
    if (dashboardMatch) {
      return dashboardMatch.panelId
        ? `${dashboardMatch.route}?highlightPanel=${encodeURIComponent(dashboardMatch.panelId)}`
        : dashboardMatch.route;
    }
    return `/visualization/${encodeURIComponent(visualizationId)}/fullscreen?datasetId=${encodeURIComponent(datasetId)}`;
  }
  return `/dataset/${encodeURIComponent(datasetId)}`;
}

/**
 * Find all tools that reference the given dataset. Tools registered with
 * `ANY_DATASET` are always returned.
 *
 * Returns: Array<{ id, labelKey, descriptionKey?, route }>
 */
export function getToolsUsingDataset(datasetId) {
  if (!datasetId) return [];
  return toolsRegistry
    .filter((tool) => tool.datasets === ANY_DATASET || tool.datasets?.includes(datasetId))
    .map(({ id, labelKey, descriptionKey, route }) => ({
      id,
      labelKey,
      descriptionKey,
      route,
    }));
}

export default {
  getDashboardsUsingDataset,
  getDashboardsUsingVisualization,
  getToolsUsingDataset,
  resolveVisualizationDeepLink,
  ANY_DATASET,
};
